/**
 * App.tsx — root + SSO bootstrap.
 * Same pattern as the React skeleton: pick up #sso=<JWT> from the URL
 * fragment (Passport login bounces here), or fall back to sessionStorage.
 * Then render Terminal.tsx.
 */
import { useEffect, useState } from 'react';
import { useStore } from '@/store';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Terminal } from '@/pages/Terminal';
import OptionsChainPage from '@/pages/OptionsChain';
import { Compliance } from '@/pages/Compliance';
import Level2Page from '@/pages/Level2';
import { fullLogout, isVaultUnlocked, lockVault, redirectToVault } from '@/lib/vault';
import { useTickStream } from '@/hooks/useTickStream';
import { FONT, MUT } from '@/theme';
import type { User } from '@/types';

const PASSPORT_ORIGIN = 'https://omnistrat.ai';

function parseJwt(token: string): User | null {
  try {
    const [, payload] = token.split('.');
    const pad = 4 - (payload.length % 4);
    const padded = payload + '='.repeat(pad % 4);
    const raw = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const ent = Array.isArray(raw.ent) ? (raw.ent as string[]) : [];
    return {
      sub:   String(raw.sub ?? ''),
      email: typeof raw.email === 'string' ? raw.email : undefined,
      ent,
      exp:   Number(raw.exp ?? 0),
    };
  } catch { return null; }
}

function useBootstrap(): { ready: boolean; signedIn: boolean } {
  const [ready, setReady] = useState(false);
  const setUser   = useStore(s => s.setUser);
  const user      = useStore(s => s.user);

  useEffect(() => {
    const now = Date.now() / 1000;
    // The ENTIRE bootstrap is wrapped so a throw (e.g. decodeURIComponent on a
    // malformed hash) can never leave the app stuck on the LOADING screen --
    // which renders as a near-black blank. `finally` guarantees setReady(true).
    try {
      const hash = window.location.hash || '';
      const ssoMatch = hash.match(/[#&]sso=([^&]+)/);
      if (ssoMatch) {
        // A raw base64url JWT has no %-escapes, so decodeURIComponent is a
        // no-op -- but if the login bounce ever URL-encodes it and produces a
        // malformed %-sequence, decode would THROW and crash the bootstrap.
        // Fall back to the raw match instead.
        let token = ssoMatch[1];
        try { token = decodeURIComponent(token); } catch { /* use the raw token */ }
        const parsed = parseJwt(token);
        // ALWAYS scrub the token from the URL -- valid or not -- so a reload can
        // never re-process a bad #sso hash and bounce forever.
        const cleanHash = hash.replace(/[#&]sso=[^&]+/, '').replace(/^&/, '#');
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search + (cleanHash === '#' ? '' : cleanHash));
        } catch { /* ignore */ }
        if (parsed && parsed.sub && (!parsed.exp || parsed.exp > now)) {
          sessionStorage.setItem('omnistrat_token', token);
          setUser(parsed, token);
          sessionStorage.removeItem('sso_bounces');   // sign-in stuck: clear loop guard
          return;                                       // finally still runs setReady(true)
        }
      }
      const stored = sessionStorage.getItem('omnistrat_token');
      if (stored) {
        const parsed = parseJwt(stored);
        if (parsed && parsed.sub && (!parsed.exp || parsed.exp > now)) {
          setUser(parsed, stored);
          sessionStorage.removeItem('sso_bounces');
        } else {
          sessionStorage.removeItem('omnistrat_token');
        }
      }
    } catch {
      // Never hang on LOADING because bootstrap threw.
    } finally {
      setReady(true);
    }
  }, [setUser]);

  return { ready, signedIn: !!user };
}

function useHashRoute(): string {
  const [route, setRoute] = useState(window.location.hash.replace(/^#/, '') || 'terminal');
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash.replace(/^#/, '') || 'terminal');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return route;
}

function ProductBar() {
  const route = useHashRoute();
  const vaultOk = isVaultUnlocked();
  // The live tick WebSocket is mounted HERE (not in Terminal) so it stays
  // connected across every route and — critically — so the LIVE pip is laid
  // out inside this flex bar and can never be clipped by an absolute overlay.
  // The hook writes ticks into the global store, so Watchlist/Movers/Chart
  // keep updating regardless of which route is mounted.
  const tick = useTickStream();
  // Compact vertical sizing so the whole bar fits INSIDE the 34px
  // tape-bar zone. Height math: 2 (container pad top) + 2 (button pad
  // top) + 18 (line-height) + 2 (button pad bot) + 2 (border) + 2
  // (container pad bot) = 28px total. At top:3 the bar spans y=3 to
  // y=31 — 3px clearance above the tape-bar's 34px bottom edge, no
  // bleed into ORDER TICKET / content panels below.
  const Tab = ({ id, label }: { id: string; label: string }) => (
    <a
      href={'#' + id}
      style={{
        padding: '2px 7px', borderRadius: 999, fontSize: 11, lineHeight: '18px', cursor: 'pointer',
        color: route === id ? '#0c0a09' : '#d6d3d1',
        background: route === id ? '#d4a45a' : 'transparent',
        textDecoration: 'none', border: '1px solid ' + (route === id ? '#d4a45a' : '#44403c'),
      }}
    >{label}</a>
  );
  return (
    <div style={{
      position: 'fixed', top: 3, right: 12, zIndex: 1000,
      display: 'flex', gap: 4, alignItems: 'center',
      background: 'rgba(10,13,10,0.85)', padding: '2px 5px', borderRadius: 8,
      backdropFilter: 'blur(8px)',
    }}>
      {/* Live-stream status pip — first child so flex reserves its width and
          it never overlaps the nav pills or gets clipped at the tape edge. */}
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 9, fontFamily: FONT, whiteSpace: 'nowrap',
        paddingRight: 4, marginRight: 2, borderRight: '1px solid #2a2724',
      }}>
        {/* "CONNECTING" forever over a feed that cannot connect (session
            expired, no live-feed entitlement) hides the reason; say it. */}
        <span style={{ color: tick.connected ? 'var(--gain)' : (tick.error ? 'var(--loss)' : MUT) }}
              title={tick.error ?? undefined}>
          {tick.connected ? '● LIVE' : tick.error ? `○ FEED OFF -- ${tick.error}` : '○ CONNECTING'}
        </span>
        {tick.latencyMs !== null && (
          <span style={{ color: MUT }}>{tick.latencyMs}ms</span>
        )}
      </span>
      <Tab id="terminal"   label="Terminal" />
      <Tab id="options"    label="Options" />
      <Tab id="level2"     label="Level 2" />
      <Tab id="compliance" label="Compliance" />
      <button
        // Was an inlined copy of lockVault() with the storage key spelled
        // out by hand — so a change to the key name in vault.ts would have
        // silently stopped this button clearing anything. Call the one
        // function that owns that knowledge.
        onClick={() => { if (vaultOk) { lockVault(); window.location.reload(); } else { redirectToVault(); } }}
        title={vaultOk ? 'Lock vault (this session)' : 'Unlock vault'}
        style={{ padding: '2px 7px', borderRadius: 999, fontSize: 11, lineHeight: '18px', cursor: 'pointer', color: vaultOk ? '#69b078' : '#c66b6b', background: 'transparent', border: '1px solid ' + (vaultOk ? 'rgba(105,176,120,0.4)' : 'rgba(198,107,107,0.4)') }}
      >{vaultOk ? 'Vault unlocked' : 'Vault locked'}</button>
      <button
        onClick={() => { void fullLogout(); }}
        title="Full logout (revokes Passport + clears vault)"
        style={{ padding: '2px 7px', borderRadius: 999, fontSize: 11, lineHeight: '18px', cursor: 'pointer', color: '#c66b6b', background: 'transparent', border: '1px solid rgba(198,107,107,0.4)' }}
      >Log out</button>
    </div>
  );
}

export function App() {
  const { ready, signedIn } = useBootstrap();
  const route = useHashRoute();

  if (!ready) {
    return (
      <div style={{
        background: '#090909', height: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: '#444', fontFamily: "Consolas, monospace",
        fontSize: 11, letterSpacing: '2px',
      }}>
        LOADING PASSPORT...
      </div>
    );
  }

  if (!signedIn) {
    // Loop guard: if sign-in keeps not sticking (a token the terminal can't
    // accept), STOP bouncing to login and show an actionable error instead of
    // an invisible infinite redirect that looks like a dead black screen.
    const bounces = Number(sessionStorage.getItem('sso_bounces') ?? '0');
    if (bounces >= 2) {
      return (
        <div style={{
          background: '#090909', height: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 12,
          color: '#c66b6b', fontFamily: FONT, fontSize: 12, letterSpacing: '1px',
        }}>
          <div>SIGN-IN DIDN&rsquo;T COMPLETE</div>
          <a
            href={`${PASSPORT_ORIGIN}/login.html?product=terminal`}
            onClick={() => sessionStorage.removeItem('sso_bounces')}
            style={{ color: '#d4a45a' }}
          >Try signing in again</a>
        </div>
      );
    }
    sessionStorage.setItem('sso_bounces', String(bounces + 1));
    // Bounce to the marketing-site Passport login.
    const next = encodeURIComponent(window.location.href);
    window.location.replace(`${PASSPORT_ORIGIN}/login.html?product=terminal&next=${next}`);
    return null;
  }

  // Each route is wrapped so a render error is contained to that panel.
  // Previously one bad field from the API unmounted the whole terminal --
  // blotter, kill switch and all -- until a manual reload. ProductBar
  // stays OUTSIDE the boundary so the vault lock and logout controls
  // survive any panel failure.
  // Top-level boundary: even a crash in ProductBar (which sits outside the
  // per-panel boundaries) now shows a visible error instead of a silent blank.
  // The inner per-panel boundaries still catch a panel error FIRST, so
  // ProductBar keeps surviving individual panel failures.
  return (
    <ErrorBoundary label="Terminal">
      <ProductBar />
      {route === 'options'    && <ErrorBoundary label="Options chain"><OptionsChainPage /></ErrorBoundary>}
      {route === 'level2'     && <ErrorBoundary label="Level 2"><Level2Page /></ErrorBoundary>}
      {route === 'compliance' && <ErrorBoundary label="Compliance"><Compliance /></ErrorBoundary>}
      {(route !== 'options' && route !== 'level2' && route !== 'compliance') && (
        <ErrorBoundary label="Terminal"><Terminal /></ErrorBoundary>
      )}
    </ErrorBoundary>
  );
}
